import type { MouseEvent } from 'react';
import type { IPartnersContact } from 'app/services/partnersApi';
import { NON_CLICKABLE_OPAQUE } from '../readOnly';

/**
 * Phase 7b — one contact row as emitted by `contactRender`
 * (`partners.js:283-310`), the hand-written HTML string a CONTACT SEARCH
 * appends. It is NOT the template row (`ContactRow.tsx`); the two have drifted
 * badly and the drift is the deliverable.
 *
 * Differences from `tmpl/partners.tmpl:222-262`, all mirrored below:
 *
 *  | | template row | search row (`partners.js:283-310`) |
 *  |---|---|---|
 *  | `data-name` on the row | `company_<co>_<ct>` (:222) | **absent** (:283) |
 *  | name cell | `div.col-xs-4.row.margin-0` → icon `<span>` + `div.col-xs-11.ml_35` | a bare `div.col-xs-4`, no icon, no offset (:284) |
 *  | `autocomplete` | `new-password` on all three | **absent** on all three |
 *  | email input | `type="email"`, `class="contact_field press_english"` | `type="text"`, `class="contact_field"` (:290) |
 *  | mobile input | `class="contact_field phone-number-input"` | `class="contact_field"` (:295) |
 *  | delete anchor | `class="remove contact_delete"` + `data-name` (:257) | `class="contact_delete remove"`, **no `data-name`** (:305) |
 *
 * Consequences, none of which are to be "fixed":
 *  - the person icon and the 35px indent disappear from every searched row;
 *  - `keypress .press_english` (`partners.js:45`) and `keypress
 *    .phone-number-input` (`:47`) stop applying, so Cyrillic and letters-in-a-
 *    phone-number become typeable in a searched row — the same shape as Q2 for
 *    a freshly-added contact;
 *  - `type="text"` on email removes the `checkValidity()` gate that
 *    `changeInputContactFieldName` (`:161`) leans on, so a searched row will
 *    PATCH a malformed address that the template row would have reverted (7c);
 *  - ★ Q9 (7c) — the missing `data-name` is what makes deleting a searched
 *    contact throw and strand an unclosable modal. 7c must NOT add it back.
 *
 * ★ WHITESPACE. Each of `<input>`, `<i>` and `<span>` is on its own line in the
 * source string, so the parsed DOM has a collapsing space text node in BOTH
 * gaps — same as the template row, reproduced with explicit `{' '}`.
 * `.text-clear` is `position:absolute !important` (styles.scss:406) but the
 * `<span>` is not, so the second gap is real inline layout.
 *
 * ★ 7c — `change .contact_field`, `click .text-clear` and `click .contact_delete`
 * all still match these rows (the classes survive) — only `data-name` is gone,
 * which is precisely why deleting one of them fires the DELETE and then throws
 * on `elem.remove()`. See `PartnersPage.deleteContact` for what is reproduced
 * and for the owner's one deliberate softening (Escape/Cancel keep working).
 * `data-name` MUST NOT be added back.
 */
interface ContactSearchRowProps {
  contact: IPartnersContact;
  /** Bootstrap's `.open` on the kebab's parent div — owned by `ContactList`. */
  open: boolean;
  onToggleMenu: (e: MouseEvent<HTMLButtonElement>, contactId: number) => void;
  /** ★ NOT A LEGACY PROP — see `ContactRow`. */
  readOnly?: boolean;
}

/** `${x ? x : ''}` in the template literal — null renders as an empty value. */
const v = (value: string | null | undefined) => value ?? '';

const ContactSearchRow = ({
  contact,
  open,
  onToggleMenu,
  readOnly = false,
}: ContactSearchRowProps) => (
  /* :283 — NO `data-name` here. That is Q9's root cause; leave it out. */
  <div className="row every-row" data-id={contact.id}>
    <div className="col-xs-4">
      <input
        className={readOnly ? 'contact_field non-clickable' : 'contact_field'}
        type="text"
        name="name"
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
        defaultValue={v(contact.name)}
        placeholder="Name"
        id="contact_info_"
      />{' '}
      <i className="text-clear icon-close-1" />{' '}
      <span className="placeholder-label placeholder-name">Name</span>
    </div>
    <div className="col-xs-4">
      {/* :290 — `type="text"`, and NO `press_english`. */}
      <input
        className={readOnly ? 'contact_field non-clickable' : 'contact_field'}
        type="text"
        name="email"
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
        defaultValue={v(contact.email)}
        placeholder="Email"
      />{' '}
      <i className="text-clear icon-close-1" />{' '}
      <span className="placeholder-label placeholder-email">Email</span>
    </div>
    <div className="col-xs-3">
      {/* :295 — NO `phone-number-input`. */}
      <input
        className={readOnly ? 'contact_field non-clickable' : 'contact_field'}
        type="text"
        name="tel_mobile"
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
        defaultValue={v(contact.tel_mobile)}
        placeholder="Mobile"
      />{' '}
      <i className="text-clear icon-close-1" />{' '}
      <span className="placeholder-label placeholder-mobile">Mobile</span>
    </div>
    {/* ★ NOT LEGACY — the kebab is not rendered at all when the owning company
        is not this user's, so it cannot appear on hover. Its only item is
        Delete, which is exactly what ownership forbids. */}
    {/* The double space in `class="dropdown  dropdown_contact col-xs-1"` is in
        the source string too (:299) — kept so the attribute matches byte for
        byte, even though the class set is identical either way. */}
    {!readOnly && (
      <div className="dropdown  dropdown_contact col-xs-1">
        <div className={open ? 'open' : ''}>
          <button
            className="btn btn-contact-delete dropdown-toggle icon-more-actions"
            type="button"
            data-toggle="dropdown"
            onClick={(e) => onToggleMenu(e, contact.id)}
          />
          <ul className="dropdown-menu">
            <li>
              {/* :305 — class order is `contact_delete remove` here and
                  `remove contact_delete` in the template. No `data-name`. */}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                tabIndex={-1}
                href="#"
                data-id={contact.id}
                className="contact_delete remove"
              >
                <i className="icon-trash" />
                Delete
              </a>
            </li>
          </ul>
        </div>
      </div>
    )}
  </div>
);

export default ContactSearchRow;
